import { useCallback, useState } from 'react';
import Dropdown from './Dropdown';
import Field from './Field';
import Icon from './Icon';
import Modal from './Modal';
import SearchableSelect from './SearchableSelect';
import { ErrorBanner } from './Feedback';
import useForm from '../hooks/useForm';
import { api } from '../api/client';
import * as s from '../validation/schoolRules';

/**
 * Add / Edit School. The same form serves both; `school` being present switches it
 * to edit and locks whatever the server says is no longer changeable.
 */
export default function SchoolFormModal({
  school,
  options,
  salesReps,
  canAssignRep,
  codeEditable = true,
  onClose,
  onSave,
}) {
  const editing = Boolean(school);
  const [error, setError] = useState(null);

  // The city and board are master rows, held as the whole `{ id, name }` so the field
  // can show the name it was opened on without a second lookup to resolve the id.
  const [city, setCity] = useState(school?.city ?? null);
  const [board, setBoard] = useState(school?.board ?? null);
  const [touchedMasters, setTouchedMasters] = useState(false);

  // Memoised, or a new function identity every render would restart the picker's
  // debounced search on each keystroke of the rest of the form.
  const searchCities = useCallback(
    (q) => api.lookupCities({ q, limit: 30 }).then((rows) =>
      rows.map((row) => ({ id: row.id, name: row.name, hint: row.stateName }))),
    [],
  );
  const loadBoards = useCallback(() => api.lookupBoards(), []);

  const form = useForm({
    initialValues: {
      name: school?.name ?? '',
      code: school?.code ?? '',
      customerStatus: school?.customerStatus ?? 'PROSPECT',
      assignedSalesRepId: school?.assignedSalesRep?.id ?? '',
    },
    validators: {
      name: s.name,
      code: s.code,
      customerStatus: s.customerStatus,
    },
  });

  const cityError = touchedMasters && !city?.id ? 'City is required' : null;
  const boardError = touchedMasters && !board?.id ? 'Board is required' : null;

  const submit = form.handleSubmit(async (values) => {
    setTouchedMasters(true);
    // The two pickers sit outside useForm — it validates text fields — so their
    // required-ness is checked here rather than being enforced only by the server.
    if (!city?.id || !board?.id) return;

    const payload = {
      name: values.name.trim(),
      code: values.code.trim() || null,
      cityId: city.id,
      boardId: board.id,
      customerStatus: values.customerStatus,
    };
    // Only a Super Admin may set the rep; sending it otherwise would be rejected.
    if (canAssignRep) payload.assignedSalesRepId = values.assignedSalesRepId || null;
    setError(null);
    try {
      await onSave(payload);
    } catch (err) {
      // Field-level rejections land on their field; anything else needs the banner.
      if (!form.applyServerErrors(err, s.SERVER_CODES)) setError(err);
    }
  });

  return (
    <Modal
      title={editing ? 'Edit school' : 'Add school'}
      subtitle={
        editing
          ? 'Changes apply to the School Master used across registration and licensing'
          : 'Leave the code blank to have one allocated'
      }
      width={660}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="school-form" className="btn btn-primary" disabled={form.submitting}>
            <Icon name="check" size={15} />
            {form.submitting ? 'Saving…' : editing ? 'Save changes' : 'Create school'}
          </button>
        </>
      }
    >
      <form id="school-form" {...form.formProps} onSubmit={submit} className="stack">
        <ErrorBanner error={error} onDismiss={() => setError(null)} />

        <Field label="School name" id="name" required error={form.errorFor('name')}>
          <input className="input" maxLength={s.LIMITS.name} {...form.field('name')} />
        </Field>

        <div className="grid-2">
          <Field
            label="School code"
            id="code"
            hint={
              !codeEditable
                ? 'Locked — teachers or students are already attached to this school'
                : editing
                  ? 'Changing this affects how the school is referenced'
                  : 'Leave blank to allocate one automatically'
            }
            error={form.errorFor('code')}
          >
            <input
              className="input"
              maxLength={s.LIMITS.code}
              placeholder="FH-DEL-0112"
              disabled={!codeEditable}
              {...form.field('code')}
            />
          </Field>
          <Field label="Board" id="board" required error={boardError}>
            <SearchableSelect
              id="board"
              value={board}
              onChange={setBoard}
              loadOptions={loadBoards}
              placeholder="Search boards"
              error={boardError}
            />
          </Field>
        </div>

        <Field
          label="City"
          id="city"
          required
          hint={
            city?.id
              ? `State: ${city.hint ?? city.stateName ?? school?.state?.name ?? '—'}`
              : 'Pick a city — its state comes with it'
          }
          error={cityError}
        >
          <SearchableSelect
            id="city"
            value={city}
            onChange={(chosen) => {
              setCity(chosen);
              setTouchedMasters(true);
            }}
            loadOptions={searchCities}
            placeholder="Search cities"
            emptyLabel="No city matches — add it under Masters"
            error={cityError}
          />
        </Field>

        <div className="grid-2">
          <Field label="Customer status" id="customerStatus" required error={form.errorFor('customerStatus')}>
            <Dropdown
              id="customerStatus"
              required
              options={options?.customerStatuses ?? []}
              value={form.values.customerStatus}
              onChange={(customerStatus) => form.setValue('customerStatus', customerStatus)}
            />
          </Field>

          {canAssignRep && (
            <Field
              label="Assigned sales representative"
              id="assignedSalesRepId"
              hint="Who owns the relationship. What a rep can see is decided by their territory, not by this."
              error={form.errorFor('assignedSalesRepId')}
            >
              <Dropdown
                id="assignedSalesRepId"
                options={salesReps.map((rep) => ({ value: rep.id, label: rep.fullName }))}
                value={form.values.assignedSalesRepId}
                onChange={(repId) => form.setValue('assignedSalesRepId', repId)}
                placeholder="Unassigned"
              />
            </Field>
          )}
        </div>
      </form>
    </Modal>
  );
}
